import { AnyAction } from 'redux';
import {
  AdressvorschlagFailedPayload,
  AdressvorschlagPayload,
  CALLER_ID,
  GlobalActions,
  ProduktDeleted,
} from '@ps20-frontend/shop-integration-redux';
import cloneDeep from 'lodash/cloneDeep';
import { updateObjectInArray } from '@ps20-frontend/reducers';
import { App } from '@ps20-frontend/frontend-models';
import { CheckoutActions } from '../checkout.actions';
import { SG_ADRESSE, Sicherungsglaeubiger } from '../model';
import { SicherungsglaeubigerVorhandenChanged } from '../action-types/sicherungsglaeubigerVorhandenChanged';
import { AddSicherungsglaeubiger } from '../action-types/addSicherungsglaeubiger';
import {
  SicherungsglaeubigerAdressArtChanged,
  SicherungsglaeubigerBanknameChanged,
  SicherungsglaeubigerHNrChanged,
  SicherungsglaeubigerOrtChanged,
  SicherungsglaeubigerPLZ,
  SicherungsglaeubigerPostfachChanged,
  SicherungsglaeubigerStrasseChanged,
} from '../action-types/sicherungsglaeubiger';

function createNewSicherungsGlaeuebiger(angebotsId: string, index: number): Sicherungsglaeubiger {
  return {
    angebotsId,
    index,
    adressArt: SG_ADRESSE.STRASSE,
    adressvorschlag: { orte: [], strassen: [], lastError: null },
    aktenzeichen: '',
    hausnr: '',
    name: '',
    ort: '',
    plz: '',
    postfach: '',
    strasse: '',
  };
}

function copySicherungsglaeubiger(
  sicherungsglaeubigerArray: Array<Sicherungsglaeubiger>,
): Array<Sicherungsglaeubiger> {
  return cloneDeep(sicherungsglaeubigerArray);
}

export const sicherungsglaeubigerReducer = function (
  sectionState: Array<Sicherungsglaeubiger> = null,
  action: AnyAction,
): Array<Sicherungsglaeubiger> {
  switch (action.type) {
    case CheckoutActions.SG_BANKNAME_CHANGED: {
      const actionPayload: SicherungsglaeubigerBanknameChanged = action.payload;
      const sicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const filteredSicherungsglaeubigerArray = sicherungsglaeubigerArray.filter(
        value => value.angebotsId === actionPayload.angebotsId,
      );
      filteredSicherungsglaeubigerArray[actionPayload.index].name = actionPayload.sgBankname;
      return sicherungsglaeubigerArray;
    }

    case CheckoutActions.SG_POSTFACH_CHANGED: {
      const actionPayload: SicherungsglaeubigerPostfachChanged = action.payload;
      const sicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const filteredSicherungsglaeubigerArray = sicherungsglaeubigerArray.filter(
        value => value.angebotsId === actionPayload.angebotsId,
      );
      filteredSicherungsglaeubigerArray[actionPayload.index].postfach = actionPayload.sgPostfach;
      return sicherungsglaeubigerArray;
    }

    case CheckoutActions.SG_PLZ_CHANGED: {
      const actionPayload: SicherungsglaeubigerPLZ = action.payload;
      const sicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const filteredSicherungsglaeubigerArray = sicherungsglaeubigerArray.filter(
        value => value.angebotsId === actionPayload.angebotsId,
      );
      filteredSicherungsglaeubigerArray[actionPayload.index].plz = actionPayload.sgPlz;
      filteredSicherungsglaeubigerArray[actionPayload.index].ort = '';
      filteredSicherungsglaeubigerArray[actionPayload.index].strasse = '';
      filteredSicherungsglaeubigerArray[actionPayload.index].hausnr = '';
      filteredSicherungsglaeubigerArray[actionPayload.index].adressvorschlag = {
        lastError: null,
        orte: [],
        strassen: [],
      };
      return sicherungsglaeubigerArray;
    }

    case GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL: {
      const payload: AdressvorschlagPayload = action.payload;
      if (payload.callerId !== CALLER_ID.SICHERUNGSLAEUBIGER) {
        return sectionState;
      }

      const sicherungsglaeubiger: Partial<Sicherungsglaeubiger> = {
        adressvorschlag: {
          lastError: null,
          orte: payload.orte ? payload.orte : [],
          strassen: payload.strassen ? payload.strassen : [],
        },
      };

      if (payload.orte?.length === 1) {
        sicherungsglaeubiger.ort = payload.orte[0];
      }

      return updateObjectInArray(sectionState, payload.callerData.index, sicherungsglaeubiger);
    }

    case CheckoutActions.SG_ORT_CHANGED: {
      const actionPayload: SicherungsglaeubigerOrtChanged = action.payload;
      const sicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const filteredSicherungsglaeubigerArray = sicherungsglaeubigerArray.filter(
        value => value.angebotsId === actionPayload.angebotsId,
      );
      filteredSicherungsglaeubigerArray[actionPayload.index].ort = actionPayload.sgOrt;
      return sicherungsglaeubigerArray;
    }

    case CheckoutActions.SG_STRASSE_CHANGED: {
      const actionPayload: SicherungsglaeubigerStrasseChanged = action.payload;
      const sicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const filteredSicherungsglaeubigerArray = sicherungsglaeubigerArray.filter(
        value => value.angebotsId === actionPayload.angebotsId,
      );
      filteredSicherungsglaeubigerArray[actionPayload.index].strasse = actionPayload.sgStrasse;
      return sicherungsglaeubigerArray;
    }

    case CheckoutActions.SG_HAUSNUMMER_CHANGED: {
      const actionPayload: SicherungsglaeubigerHNrChanged = action.payload;
      const sicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const filteredSicherungsglaeubigerArray = sicherungsglaeubigerArray.filter(
        value => value.angebotsId === actionPayload.angebotsId,
      );
      filteredSicherungsglaeubigerArray[actionPayload.index].hausnr = actionPayload.sgHausnummer;
      return sicherungsglaeubigerArray;
    }

    case CheckoutActions.SG_AKTENZEICHEN_CHANGED: {
      const sicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const actionPayload = action.payload;
      const filteredSicherungsglaeubigerArray = sicherungsglaeubigerArray.filter(
        value => value.angebotsId === actionPayload.angebotsId,
      );
      filteredSicherungsglaeubigerArray[actionPayload.index].aktenzeichen =
        actionPayload.sgAktenzeichen;
      return sicherungsglaeubigerArray;
    }

    case CheckoutActions.SG_ADRESSART_CHANGED: {
      const sicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const actionPayload: SicherungsglaeubigerAdressArtChanged = action.payload;
      const filteredSicherungsglaeubigerArray = sicherungsglaeubigerArray.filter(
        value => value.angebotsId === actionPayload.angebotsId,
      );
      filteredSicherungsglaeubigerArray[actionPayload.index].adressArt = actionPayload.adressArt;
      filteredSicherungsglaeubigerArray[actionPayload.index].postfach = '';
      filteredSicherungsglaeubigerArray[actionPayload.index].plz = '';
      filteredSicherungsglaeubigerArray[actionPayload.index].ort = '';
      filteredSicherungsglaeubigerArray[actionPayload.index].strasse = '';
      filteredSicherungsglaeubigerArray[actionPayload.index].hausnr = '';
      filteredSicherungsglaeubigerArray[actionPayload.index].adressvorschlag = {
        lastError: null,
        orte: [],
        strassen: [],
      };

      return sicherungsglaeubigerArray;
    }

    case GlobalActions.ADDRESSVORSCHLAG_FAILED: {
      const payload: AdressvorschlagFailedPayload = action.payload;
      if (payload.callerId !== CALLER_ID.SICHERUNGSLAEUBIGER) {
        return sectionState;
      }
      const sicherungsglaeubiger: Partial<Sicherungsglaeubiger> = {
        adressvorschlag: { lastError: payload.reason, orte: [], strassen: [] },
      };

      return updateObjectInArray(sectionState, payload.callerData.index, sicherungsglaeubiger);
    }

    case GlobalActions.PRODUKT_DELETED: {
      const actionPayload: ProduktDeleted = action.payload;
      return sectionState.filter(
        sicherungsglaeubiger =>
          !actionPayload.angebotsIds.some(
            angebotsId =>
              angebotsId.appId === App.VWG &&
              sicherungsglaeubiger.angebotsId === angebotsId.angebotsId,
          ),
      );
    }

    case CheckoutActions.SICHERUNGSGLAEUBIGER_VORHANDEN_CHANGED: {
      const newSicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const actionPayload: SicherungsglaeubigerVorhandenChanged = action.payload;
      if (
        actionPayload.changed &&
        sectionState.filter(value => value.angebotsId === actionPayload.angebotsId).length === 0
      ) {
        newSicherungsglaeubigerArray.push(
          createNewSicherungsGlaeuebiger(actionPayload.angebotsId, 0),
        );
      } else if (!actionPayload.changed) {
        return newSicherungsglaeubigerArray.filter(
          value => value.angebotsId !== actionPayload.angebotsId,
        );
      }
      return newSicherungsglaeubigerArray;
    }

    case CheckoutActions.DELETE_SICHERUNGSGLAEUBIGER: {
      const resultArray = copySicherungsglaeubiger(sectionState).filter((elem, indexInArray) => {
        const siglIndex = sectionState.findIndex(
          sigl =>
            sigl.angebotsId === action.payload.angebotsId && sigl.index === action.payload.index,
        );
        return siglIndex !== indexInArray;
      });
      resultArray
        .filter(sigl => sigl.angebotsId === action.payload.angebotsId)
        .forEach((sigl, indexInArray) => (sigl.index = indexInArray));
      return resultArray;
    }

    case CheckoutActions.ADD_SICHERUNGSGLAEUBIGER: {
      const newSicherungsglaeubigerArray = copySicherungsglaeubiger(sectionState);
      const actionPayload: AddSicherungsglaeubiger = action.payload;
      const index: number = newSicherungsglaeubigerArray.filter(
        sigl => sigl.angebotsId === actionPayload.angebotsId,
      ).length;
      newSicherungsglaeubigerArray.push(
        createNewSicherungsGlaeuebiger(actionPayload.angebotsId, index),
      );

      return newSicherungsglaeubigerArray;
    }

    default:
      return sectionState;
  }
};
